import { useCallback, useEffect, useState } from "react";
import { api, notifyError } from "@/lib/api";
import { FloppyDisk, ShieldWarning, SealCheck, CheckCircle, ToggleLeft, ToggleRight, Bank, MagnifyingGlass, Trash } from "@phosphor-icons/react";
import MigrationPanel from "@/pages/compliance/MigrationPanel";

const RAG = {
  green: { c: "#00A86B", label: "On track" },
  amber: { c: "#FFB020", label: "Due soon" },
  red: { c: "#E63946", label: "Overdue" },
  acknowledged: { c: "#00A86B", label: "Filed" },
};

const UK_FIELDS = { uk_enabled: false, uk_incorporation_date: "", uk_ard: "", uk_vat_registered: false, uk_vat_quarter_end: "3" };
const US_FIELDS = { us_enabled: false, us_state: "OH", us_incorporation_date: "", us_entity_type: "c_corp", us_fye: "12-31", us_cat_registered: false, us_sales_tax_registered: false, us_income_tax_registered: false, us_withholding_registered: false, us_annual_report_registered: false, us_hipaa: false };
const US_ENTITY_TYPES = [
  { id: "c_corp", label: "C-Corporation" },
  { id: "s_corp", label: "S-Corporation" },
  { id: "llc", label: "LLC" },
];
const VAT_QUARTERS = [
  { id: "3", label: "Mar / Jun / Sep / Dec" },
  { id: "1", label: "Jan / Apr / Jul / Oct" },
  { id: "2", label: "Feb / May / Aug / Nov" },
];

export default function CompanyTab({ onSaved }) {
  const [full, setFull] = useState({});
  const [form, setForm] = useState({ trade_name: "", tax_reference: "", commencement_date: "", incorporation_date: "", ...UK_FIELDS, ...US_FIELDS });
  const [windowStart, setWindowStart] = useState("");
  const [cro, setCro] = useState(null);
  const [uk, setUk] = useState(null);
  const [us, setUs] = useState(null);
  const [stateRegs, setStateRegs] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [chNum, setChNum] = useState("");
  const [chLoading, setChLoading] = useState(false);
  const [chResult, setChResult] = useState(null);
  const [notif, setNotif] = useState({ deadline_reminders_enabled: false, deadline_reminder_days: [14, 3], baa_reminder_days: [30, 7], nexus_alerts_enabled: false, auto_import_enabled: false });
  const [notifSaving, setNotifSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [hipaa, setHipaa] = useState({ steps: [], done: 0, total: 0, note: "" });
  const [baas, setBaas] = useState([]);
  const [baaForm, setBaaForm] = useState({ vendor: "", signed_date: "", review_date: "" });

  const loadBaas = useCallback(async () => {
    try { const r = await api.get("/compliance/us/baa"); setBaas(r.data.items || []); } catch { /* ignore */ }
  }, []);
  const uploadBaa = async (file) => {
    if (!baaForm.vendor.trim()) { setMsg({ type: "err", text: "Enter the vendor name first." }); return; }
    const b64 = await new Promise((res) => { const rd = new FileReader(); rd.onload = () => res(rd.result); rd.readAsDataURL(file); });
    try {
      await api.post("/compliance/us/baa", { vendor: baaForm.vendor, signed_date: baaForm.signed_date, review_date: baaForm.review_date, name: file.name, file_mime: file.type || "application/pdf", file_base64: b64 });
      setBaaForm({ vendor: "", signed_date: "", review_date: "" }); loadBaas();
      setMsg({ type: "ok", text: "BAA saved to the vault." });
    } catch { setMsg({ type: "err", text: "Couldn't save the BAA." }); }
  };
  const downloadBaa = async (b) => {
    const r = await api.get(`/compliance/us/baa/${b.baa_id}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = b.name || "BAA"; a.click(); URL.revokeObjectURL(url);
  };
  const deleteBaa = async (id) => { await api.delete(`/compliance/us/baa/${id}`); loadBaas(); };
  const reviewBaa = async (b) => {
    try {
      const r = await api.post(`/compliance/us/baa/${b.baa_id}/review`, { months: 12 });
      setMsg({ type: "ok", text: `${b.vendor} marked reviewed — next review ${r.data.review_date}.` });
      loadBaas();
    } catch { setMsg({ type: "err", text: "Couldn't mark this BAA reviewed." }); }
  };

  const toggleHipaa = async (stepId, done) => {
    setHipaa((h) => {
      const steps = h.steps.map((s) => s.id === stepId ? { ...s, done } : s);
      return { ...h, steps, done: steps.filter((s) => s.done).length };
    });
    try { await api.post("/compliance/checklist/toggle", { event_key: "HIPAA", step: stepId, done }); }
    catch { try { const r = await api.get("/compliance/us/hipaa"); setHipaa(r.data); } catch { /* keep */ } }
  };

  const loadCro = useCallback(async () => {
    try { const r = await api.get("/compliance/cro"); setCro(r.data); } catch { setCro(null); }
  }, []);
  const loadUk = useCallback(async () => {
    try { const r = await api.get("/compliance/uk"); setUk(r.data); } catch { setUk(null); }
  }, []);
  const loadUs = useCallback(async () => {
    try { const r = await api.get("/compliance/us"); setUs(r.data); } catch { setUs(null); }
  }, []);

  const hydrate = useCallback((d) => {
    setFull(d);
    const legacyFlags = {};
    ["us_cat_registered", "us_sales_tax_registered", "us_income_tax_registered", "us_withholding_registered", "us_annual_report_registered"].forEach((k) => { if (d[k]) legacyFlags[k] = true; });
    const usStates = (Array.isArray(d.us_states) && d.us_states.length)
      ? d.us_states.map((it) => ({ state: it.state, flags: { ...(it.flags || {}) }, sales: it.sales ?? "", transactions: it.transactions ?? "" }))
      : (d.us_enabled ? [{ state: d.us_state || "OH", flags: legacyFlags, sales: "", transactions: "" }] : []);
    setForm({
      trade_name: d.trade_name || "", tax_reference: d.tax_reference || "",
      commencement_date: d.commencement_date || "", incorporation_date: d.incorporation_date || "",
      uk_enabled: !!d.uk_enabled, uk_incorporation_date: d.uk_incorporation_date || "",
      uk_ard: d.uk_ard || "", uk_vat_registered: !!d.uk_vat_registered, uk_vat_quarter_end: d.uk_vat_quarter_end || "3",
      us_enabled: !!d.us_enabled, us_state: d.us_state || "OH", us_incorporation_date: d.us_incorporation_date || "",
      us_entity_type: d.us_entity_type || "c_corp", us_fye: d.us_fye || "12-31",
      us_cat_registered: !!d.us_cat_registered, us_sales_tax_registered: !!d.us_sales_tax_registered,
      us_income_tax_registered: !!d.us_income_tax_registered, us_withholding_registered: !!d.us_withholding_registered,
      us_annual_report_registered: !!d.us_annual_report_registered, us_states: usStates, us_hipaa: !!d.us_hipaa,
    });
    setWindowStart(d.window_start || "");
  }, []);

  useEffect(() => {
    (async () => {
      const r = await api.get("/compliance/company");
      hydrate(r.data);
      loadCro(); loadUk(); loadUs();
      try { const n = await api.get("/compliance/notifications"); setNotif(n.data); } catch { /* defaults */ }
      try { const h = await api.get("/compliance/us/hipaa"); setHipaa(h.data); } catch { /* defaults */ }
      loadBaas();
    })();
  }, [hydrate, loadCro, loadUk, loadUs, loadBaas]);

  useEffect(() => {
    if (!form.us_enabled) return;
    const codes = (form.us_states || []).map((s) => s.state);
    const missing = codes.filter((c) => c && !stateRegs[c]);
    if (missing.length === 0) return;
    let active = true;
    Promise.all(missing.map((c) => api.get(`/compliance/us/registrations/${c}`).then((r) => [c, r.data]).catch(() => [c, { registrations: [], nexus: "", threshold: null }])))
      .then((pairs) => { if (active) setStateRegs((prev) => ({ ...prev, ...Object.fromEntries(pairs) })); });
    return () => { active = false; };
  }, [form.us_states, form.us_enabled, stateRegs]);

  const addUsState = (code) => {
    if (!code || (form.us_states || []).some((s) => s.state === code)) return;
    setForm({ ...form, us_states: [...(form.us_states || []), { state: code, flags: {}, sales: "", transactions: "" }] });
  };
  const removeUsState = (code) => setForm({ ...form, us_states: (form.us_states || []).filter((s) => s.state !== code) });
  const toggleUsFlag = (code, flag) => setForm({
    ...form,
    us_states: (form.us_states || []).map((s) => s.state === code ? { ...s, flags: { ...s.flags, [flag]: !s.flags[flag] } } : s),
  });
  const setUsField = (code, field, value) => setForm({
    ...form,
    us_states: (form.us_states || []).map((s) => s.state === code ? { ...s, [field]: value } : s),
  });

  const save = async (e) => {
    if (e) e.preventDefault();
    setSaving(true); setMsg(null);
    try {
      const r = await api.put("/compliance/company", { ...full, ...form });  // merge preserves VAT/CT/registration settings
      hydrate(r.data);
      setMsg({ type: "ok", text: "Company profile saved." });
      loadCro(); loadUk(); loadUs(); onSaved && onSaved();
    } catch (err) {
      setMsg({ type: "err", text: err?.response?.data?.detail || "Save failed." });
    } finally { setSaving(false); }
  };

  const acknowledge = async (deadline) => {
    await api.post("/compliance/cro/acknowledge", { filing_deadline: deadline });
    loadCro(); onSaved && onSaved();
  };
  const ackUk = async (ackKey) => {
    const r = await api.post("/compliance/uk/acknowledge", { ack_key: ackKey });
    setUk(r.data); onSaved && onSaved();
  };
  const ackUs = async (ackKey) => {
    const r = await api.post("/compliance/us/acknowledge", { ack_key: ackKey });
    setUs(r.data); onSaved && onSaved();
  };

  const saveNotif = async (next) => {
    const body = next || notif;
    setNotifSaving(true); setMsg(null);
    try { const r = await api.put("/compliance/notifications", body); setNotif(r.data); setMsg({ type: "ok", text: "Reminder settings saved." }); }
    catch { setMsg({ type: "err", text: "Couldn't save reminder settings." }); }
    finally { setNotifSaving(false); }
  };
  const sendTestReminder = async () => {
    setMsg(null);
    try {
      const r = await api.post("/compliance/reminders/send-now");
      setMsg({ type: "ok", text: r.data.count ? `Reminder prepared for ${r.data.count} deadline(s).` : "No deadlines are due for a reminder right now." });
    } catch { setMsg({ type: "err", text: "Couldn't send a test reminder." }); }
  };
  const importStateSales = async () => {
    setImporting(true); setMsg(null);
    try {
      const r = await api.get("/compliance/us/state-sales");
      const by = r.data.by_state || {};
      const hit = (form.us_states || []).filter((s) => by[s.state]).length;
      setForm((f) => ({ ...f, us_states: (f.us_states || []).map((s) => by[s.state] ? { ...s, sales: by[s.state].sales, transactions: by[s.state].transactions } : s) }));
      setMsg({ type: hit ? "ok" : "err", text: hit ? `Imported ${r.data.period} sales for ${hit} state(s) from your invoices — review, then Save US settings.` : "No invoices matched your tracked states. Tag clients with a US state in the Invoices → Clients directory first." });
    } catch { setMsg({ type: "err", text: "Couldn't import from invoices." }); }
    finally { setImporting(false); }
  };

  const lookupUk = async () => {    if (!chNum.trim()) return;
    setChLoading(true); setChResult(null);
    try {
      const r = await api.get(`/compliance/uk/lookup/${encodeURIComponent(chNum.trim())}`);
      setChResult(r.data);
      setForm((f) => ({
        ...f,
        uk_incorporation_date: r.data.uk_incorporation_date || f.uk_incorporation_date,
        uk_ard: r.data.uk_ard || f.uk_ard,
      }));
    } catch (e) { notifyError(e); }
    finally { setChLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <div className="glass rounded-2xl p-8">
          <div className="eyebrow mb-1">General business information</div>
          <div className="font-display text-2xl mb-6">Company setup</div>

          {msg && <div className={`mb-5 text-sm rounded-lg px-4 py-3 ${msg.type === "ok" ? "badge-pass" : "badge-fail"}`} data-testid="company-msg">{msg.text}</div>}

          <form onSubmit={save} className="space-y-5">
            <div><label>Company / Trade name</label>
              <input data-testid="company-trade-name" value={form.trade_name} onChange={(e) => setForm({ ...form, trade_name: e.target.value })} placeholder="e.g. Acme Health Ltd" />
            </div>
            <div><label>CRO number / Tax Reference</label>
              <input data-testid="company-tax-ref" value={form.tax_reference} onChange={(e) => setForm({ ...form, tax_reference: e.target.value })} placeholder="e.g. CRO 654321 or IE1234567" />
              <div className="flex items-center gap-2 text-[11px] text-[#FFB020] mt-2"><ShieldWarning size={14} weight="fill" /> For GDPR, do not enter a PPSN — it will be rejected and never stored.</div>
            </div>
            <div><label>Date of incorporation (CRO)</label>
              <input type="date" data-testid="company-incorporation" value={form.incorporation_date} onChange={(e) => setForm({ ...form, incorporation_date: e.target.value })} />
            </div>
            <div><label>Trade commencement date</label>
              <input type="date" data-testid="company-commencement" value={form.commencement_date} onChange={(e) => setForm({ ...form, commencement_date: e.target.value })} />
            </div>

            <div className="rounded-xl bg-black/30 border border-white/5 p-5">
              <div className="eyebrow mb-1">Pre-Trading window (S.82)</div>
              <div className="text-sm text-white/70">Eligible from <span className="mono text-white" data-testid="window-start">{windowStart || "—"}</span> up to commencement (3-year rule).</div>
            </div>

            <div className="flex justify-end pt-2 border-t border-white/5">
              <button type="submit" disabled={saving} data-testid="company-save" className="btn-gold"><FloppyDisk size={16} weight="fill" /> {saving ? "Saving…" : "Save profile"}</button>
            </div>
          </form>
        </div>

        <CroCard cro={cro} onAcknowledge={acknowledge} />
      </div>

      <MigrationPanel onCroChange={loadCro} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <div className="glass rounded-2xl p-8" data-testid="uk-entity-form">
          <div className="flex items-center gap-2 eyebrow mb-1"><Bank size={14} /> UK entity · Companies House &amp; HMRC</div>
          <div className="font-display text-2xl mb-2">UK compliance</div>
          <p className="text-sm text-white/55 mb-5">Track a UK entity alongside your Irish company — Confirmation Statement, annual accounts, Corporation Tax and MTD VAT deadlines.</p>

          <button onClick={() => setForm({ ...form, uk_enabled: !form.uk_enabled })} data-testid="uk-enabled-toggle"
            className="flex items-center gap-3 w-full rounded-xl bg-black/30 border border-white/5 p-4 mb-5 hover:border-white/15">
            {form.uk_enabled ? <ToggleRight size={28} weight="fill" color="#5B8DEF" /> : <ToggleLeft size={28} color="#8A9A94" />}
            <div className="text-left">
              <div className="text-sm font-semibold">{form.uk_enabled ? "UK entity tracked" : "No UK entity"}</div>
              <div className="text-xs text-white/50">Enable to add UK statutory deadlines to your calendar.</div>
            </div>
          </button>

          {form.uk_enabled && (
            <div className="space-y-5">
              <div>
                <label>Look up on Companies House</label>
                <div className="flex gap-2">
                  <input data-testid="uk-ch-number" value={chNum} onChange={(e) => setChNum(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupUk(); } }}
                    placeholder="Company number e.g. 10636320" className="flex-1" />
                  <button type="button" onClick={lookupUk} disabled={chLoading} data-testid="uk-ch-lookup" className="btn-gold shrink-0">
                    <MagnifyingGlass size={16} weight="bold" /> {chLoading ? "Looking…" : "Look up"}
                  </button>
                </div>
                {chResult && (
                  <div className="mt-2 text-xs rounded-lg px-3 py-2 badge-pass" data-testid="uk-ch-result">
                    Found <strong>{chResult.company_name}</strong> ({chResult.company_number}) · {chResult.status}. Incorporation date &amp; ARD prefilled below — review, then Save.
                  </div>
                )}
                <p className="text-[11px] text-white/40 mt-1.5">Auto-fills incorporation date &amp; accounting reference date from the official register.</p>
              </div>
              <div><label>UK incorporation date (Companies House)</label>
                <input type="date" data-testid="uk-incorporation" value={form.uk_incorporation_date} onChange={(e) => setForm({ ...form, uk_incorporation_date: e.target.value })} />
              </div>
              <div><label>Accounting Reference Date (optional, MM-DD)</label>
                <input data-testid="uk-ard" value={form.uk_ard} onChange={(e) => setForm({ ...form, uk_ard: e.target.value })} placeholder="e.g. 09-30 (defaults to end of incorporation month)" />
              </div>
              <button onClick={() => setForm({ ...form, uk_vat_registered: !form.uk_vat_registered })} data-testid="uk-vat-toggle"
                className="flex items-center gap-3 w-full rounded-xl bg-black/30 border border-white/5 p-4 hover:border-white/15">
                {form.uk_vat_registered ? <ToggleRight size={24} weight="fill" color="#5B8DEF" /> : <ToggleLeft size={24} color="#8A9A94" />}
                <span className="text-sm font-semibold">{form.uk_vat_registered ? "UK VAT registered (MTD)" : "Not UK VAT registered"}</span>
              </button>
              {form.uk_vat_registered && (
                <div><label>VAT quarter ending months</label>
                  <div className="grid grid-cols-1 gap-2 mt-2">
                    {VAT_QUARTERS.map((q) => (
                      <button key={q.id} onClick={() => setForm({ ...form, uk_vat_quarter_end: q.id })} data-testid={`uk-vat-q-${q.id}`}
                        className={`text-left rounded-xl p-3 border transition-colors ${form.uk_vat_quarter_end === q.id ? "border-[#5B8DEF] bg-[#5B8DEF]/10" : "border-white/8 bg-black/20 hover:border-white/20"}`}>
                        <div className="text-sm font-semibold">{q.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end pt-5 mt-5 border-t border-white/5">
            <button onClick={() => save()} disabled={saving} data-testid="uk-save" className="btn-gold"><FloppyDisk size={16} weight="fill" /> {saving ? "Saving…" : "Save UK settings"}</button>
          </div>
        </div>

        <UkCard uk={uk} enabled={form.uk_enabled} onAcknowledge={ackUk} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <div className="glass rounded-2xl p-8" data-testid="us-entity-form">
          <div className="flex items-center gap-2 eyebrow mb-1"><Bank size={14} /> US entity · IRS &amp; state</div>
          <div className="font-display text-2xl mb-2">US compliance</div>
          <p className="text-sm text-white/55 mb-5">Track a US operation in any of the 50 states (or DC) alongside your Irish company — Federal Form 1120, estimated tax, plus that state&apos;s sales &amp; use tax, income/franchise tax, employer withholding and annual report. Mileage &amp; expenses follow the <strong>employer entity&apos;s</strong> jurisdiction (IRS rules), not the trip location.</p>

          <button onClick={() => setForm({ ...form, us_enabled: !form.us_enabled })} data-testid="us-enabled-toggle"
            className="flex items-center gap-3 w-full rounded-xl bg-black/30 border border-white/5 p-4 mb-5 hover:border-white/15">
            {form.us_enabled ? <ToggleRight size={28} weight="fill" color="#C77DFF" /> : <ToggleLeft size={28} color="#8A9A94" />}
            <div className="text-left">
              <div className="text-sm font-semibold">{form.us_enabled ? "US entity tracked" : "No US entity"}</div>
              <div className="text-xs text-white/50">Enable to add US federal &amp; state deadlines to your calendar.</div>
            </div>
          </button>

          {form.us_enabled && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <div><label>US incorporation date</label>
                  <input type="date" data-testid="us-incorporation" value={form.us_incorporation_date} onChange={(e) => setForm({ ...form, us_incorporation_date: e.target.value })} />
                </div>
                <div><label>Fiscal year end (MM-DD)</label>
                  <input data-testid="us-fye" value={form.us_fye} onChange={(e) => setForm({ ...form, us_fye: e.target.value })} placeholder="12-31" />
                </div>
              </div>
              <div><label>Entity type</label>
                <select data-testid="us-entity-type" value={form.us_entity_type} onChange={(e) => setForm({ ...form, us_entity_type: e.target.value })}>
                  {US_ENTITY_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>

              <div className="space-y-4" data-testid="us-states-manager">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-wider text-white/40">States you operate in</div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={importStateSales} disabled={importing || (form.us_states || []).length === 0} data-testid="us-import-sales" className="btn-ghost text-xs">
                      {importing ? "Importing…" : "Import sales from invoices"}
                    </button>
                    <select data-testid="us-add-state" value="" onChange={(e) => { addUsState(e.target.value); e.target.value = ""; }}
                      className="text-sm rounded-lg bg-black/30 border border-white/10 px-2 py-1.5" style={{ width: "auto" }}>
                      <option value="">+ Add a state…</option>
                      {(us?.states || []).filter((s) => !(form.us_states || []).some((x) => x.state === s.code)).map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                    </select>
                  </div>
                </div>

                {(form.us_states || []).length === 0 && <div className="text-xs text-white/40">Add a state to track its filing deadlines.</div>}

                {(form.us_states || []).map((entry) => {
                  const meta = stateRegs[entry.state] || { registrations: [], nexus: "" };
                  const name = (us?.states || []).find((s) => s.code === entry.state)?.name || entry.state;
                  return (
                    <div key={entry.state} className="rounded-xl bg-black/20 border border-white/8 p-4" data-testid={`us-state-block-${entry.state}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-semibold">{name}</div>
                        <button onClick={() => removeUsState(entry.state)} data-testid={`us-remove-${entry.state}`} className="text-white/40 hover:text-[#E63946] text-xs">Remove</button>
                      </div>
                      <div className="space-y-2">
                        {(meta.registrations || []).map((r) => (
                          <button key={r.flag} onClick={() => toggleUsFlag(entry.state, r.flag)} data-testid={`us-reg-${entry.state}-${r.flag}`}
                            className="flex items-center gap-3 w-full rounded-lg bg-black/30 border border-white/5 p-3 hover:border-white/15">
                            {entry.flags[r.flag] ? <ToggleRight size={22} weight="fill" color="#C77DFF" /> : <ToggleLeft size={22} color="#8A9A94" />}
                            <div className="text-left"><div className="text-sm">{r.label}</div>
                              <div className="text-[11px] text-white/50">{entry.flags[r.flag] ? "Registered — reminders on" : "Not registered"}</div></div>
                          </button>
                        ))}
                        {(meta.registrations || []).length === 0 && <div className="text-xs text-white/40">Loading registrations…</div>}
                      </div>
                      {meta.nexus && <div className="text-[11px] text-white/45 mt-3 leading-relaxed" data-testid={`us-nexus-${entry.state}`}><strong className="text-white/60">When it applies:</strong> {meta.nexus}</div>}
                      <NexusTracker state={entry.state} threshold={meta.threshold} sales={entry.sales} transactions={entry.transactions}
                        onChange={(field, value) => setUsField(entry.state, field, value)} />
                    </div>
                  );
                })}
              </div>

              <div className="rounded-xl bg-black/20 border border-white/8 p-4" data-testid="us-hipaa-card">
                <button type="button" onClick={() => setForm({ ...form, us_hipaa: !form.us_hipaa })} data-testid="toggle-hipaa"
                  className="flex items-center gap-3 w-full text-left">
                  {form.us_hipaa ? <ToggleRight size={24} weight="fill" color="#4ECDC4" /> : <ToggleLeft size={24} color="#8A9A94" />}
                  <div><div className="text-sm font-semibold">Handles health data (HIPAA)</div>
                    <div className="text-[11px] text-white/50">Turn on if you process Protected Health Information (e.g. an NIH/SBIR health project).</div></div>
                </button>
                {form.us_hipaa && (
                  <div className="mt-4 pt-3 border-t border-white/5" data-testid="hipaa-checklist">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-wider text-white/40">HIPAA readiness checklist</div>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/10 text-white/70" data-testid="hipaa-progress">{hipaa.done}/{hipaa.total || 6}</span>
                    </div>
                    <div className="space-y-1.5">
                      {hipaa.steps.map((s) => (
                        <button type="button" key={s.id} onClick={() => toggleHipaa(s.id, !s.done)}
                          className="w-full flex items-center gap-2.5 text-left py-1" data-testid={`hipaa-step-${s.id}`}>
                          {s.done ? <CheckCircle size={18} weight="fill" color="#4ECDC4" /> : <SealCheck size={18} color="#8A9A94" />}
                          <span className={`text-sm ${s.done ? "text-white/40 line-through" : "text-white/80"}`}>{s.label}</span>
                        </button>
                      ))}
                    </div>
                    {hipaa.note && <div className="text-[11px] text-white/40 mt-3 leading-relaxed">{hipaa.note}</div>}

                    <div className="mt-5 pt-4 border-t border-white/5" data-testid="baa-vault">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] uppercase tracking-wider text-white/40">BAA vault</div>
                        <span className="text-[10px] text-white/45">{baas.length} on file</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
                        <input placeholder="Vendor / business associate" value={baaForm.vendor} onChange={e => setBaaForm({ ...baaForm, vendor: e.target.value })} data-testid="baa-vendor" />
                        <input type="date" title="Signed date" value={baaForm.signed_date} onChange={e => setBaaForm({ ...baaForm, signed_date: e.target.value })} data-testid="baa-date" />
                        <input type="date" title="Renewal / review date" placeholder="Review date" value={baaForm.review_date} onChange={e => setBaaForm({ ...baaForm, review_date: e.target.value })} data-testid="baa-review-date" />
                        <label className="btn-ghost text-xs flex items-center justify-center cursor-pointer" data-testid="baa-upload-label">
                          Upload signed BAA
                          <input type="file" className="hidden" data-testid="baa-file" onChange={e => { if (e.target.files?.[0]) { uploadBaa(e.target.files[0]); e.target.value = ""; } }} />
                        </label>
                      </div>
                      {baas.length === 0 ? (
                        <div className="text-[11px] text-white/40">No agreements stored yet. Upload signed BAAs so your HIPAA evidence lives alongside the checklist.</div>
                      ) : (
                        <div className="space-y-1.5">
                          {baas.map((b) => (
                            <div key={b.baa_id} className="flex items-center gap-3 rounded-lg bg-black/25 border border-white/5 p-2.5" data-testid={`baa-${b.baa_id}`}>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm truncate flex items-center gap-2">
                                  {b.vendor}
                                  {b.status === "overdue" && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ background: "#E6394622", color: "#E63946" }} data-testid={`baa-badge-${b.baa_id}`}>Review overdue</span>}
                                  {b.status === "expiring_soon" && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ background: "#FFB02022", color: "#FFB020" }} data-testid={`baa-badge-${b.baa_id}`}>Expiring soon</span>}
                                  {b.status === "ok" && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ background: "#00A86B22", color: "#00A86B" }} data-testid={`baa-badge-${b.baa_id}`}>Current</span>}
                                </div>
                                <div className="text-[11px] text-white/45 truncate">{b.name}{b.signed_date ? ` · signed ${b.signed_date}` : ""}{b.review_date ? ` · review ${b.review_date}` : ""}{b.last_reviewed ? ` · reviewed ${b.last_reviewed}` : ""}</div>
                              </div>
                              <button type="button" onClick={() => reviewBaa(b)} className="text-[#00A86B] hover:text-white text-xs whitespace-nowrap" data-testid={`baa-review-${b.baa_id}`} title="Record today as reviewed and push the next review out 12 months">Mark reviewed ↻</button>
                              <button type="button" onClick={() => downloadBaa(b)} className="text-white/60 hover:text-white text-xs" data-testid={`baa-download-${b.baa_id}`}>Download</button>
                              <button type="button" onClick={() => deleteBaa(b.baa_id)} className="text-white/40 hover:text-[#E63946]" data-testid={`baa-del-${b.baa_id}`}><Trash size={14} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="text-[11px] text-white/40">Indicative reminders only — US federal &amp; state rules vary by entity type and thresholds, and dates change. Confirm each obligation with the state and a US CPA.</div>
            </div>
          )}
          <div className="flex justify-end pt-5 mt-5 border-t border-white/5">
            <button onClick={() => save()} disabled={saving} data-testid="us-save" className="btn-gold"><FloppyDisk size={16} weight="fill" /> {saving ? "Saving…" : "Save US settings"}</button>
          </div>
        </div>

        <UsCard us={us} enabled={form.us_enabled} onAcknowledge={ackUs} />
      </div>

      <div className="glass rounded-2xl p-8" data-testid="reminders-card">
        <div className="flex items-center gap-2 eyebrow mb-1"><ShieldWarning size={14} /> Reminders &amp; alerts</div>
        <div className="font-display text-2xl mb-2">Email reminders &amp; alerts</div>
        <p className="text-sm text-white/55 mb-5">Get emailed ahead of statutory filing deadlines and when your US sales approach a state&apos;s registration threshold. Sent to your account email.</p>

        <button type="button" onClick={() => saveNotif({ ...notif, deadline_reminders_enabled: !notif.deadline_reminders_enabled })} data-testid="toggle-deadline-reminders"
          className="flex items-center gap-3 w-full rounded-xl bg-black/30 border border-white/5 p-4 mb-3 hover:border-white/15">
          {notif.deadline_reminders_enabled ? <ToggleRight size={26} weight="fill" color="#C9A24B" /> : <ToggleLeft size={26} color="#8A9A94" />}
          <div className="text-left"><div className="text-sm font-semibold">Deadline reminders</div>
            <div className="text-xs text-white/50">Email me before each IE / UK / US filing deadline.</div></div>
        </button>

        {notif.deadline_reminders_enabled && (
          <div className="mb-3 rounded-xl bg-black/20 border border-white/8 p-4" data-testid="reminder-days-editor">
            <label className="text-[11px] uppercase tracking-wider text-white/40">Remind me this many days before (up to two nudges)</label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <input type="number" min="1" max="90" data-testid="reminder-days-0" value={notif.deadline_reminder_days?.[0] ?? 14}
                onChange={(e) => setNotif({ ...notif, deadline_reminder_days: [Number(e.target.value) || 0, notif.deadline_reminder_days?.[1] ?? 3] })} />
              <input type="number" min="1" max="90" data-testid="reminder-days-1" value={notif.deadline_reminder_days?.[1] ?? 3}
                onChange={(e) => setNotif({ ...notif, deadline_reminder_days: [notif.deadline_reminder_days?.[0] ?? 14, Number(e.target.value) || 0] })} />
            </div>
            <div className="flex gap-2 mt-3">
              <button type="button" onClick={() => saveNotif()} disabled={notifSaving} data-testid="reminder-days-save" className="btn-gold text-xs"><FloppyDisk size={14} weight="fill" /> {notifSaving ? "Saving…" : "Save schedule"}</button>
              <button type="button" onClick={sendTestReminder} data-testid="reminder-test" className="btn-ghost text-xs">Send test now</button>
            </div>

            <div className="mt-4 pt-4 border-t border-white/8" data-testid="baa-reminder-editor">
              <label className="text-[11px] uppercase tracking-wider text-white/40">BAA renewal reminders — days before each HIPAA Business Associate Agreement review (up to two nudges)</label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <input type="number" min="1" max="90" data-testid="baa-reminder-days-0" value={notif.baa_reminder_days?.[0] ?? 30}
                  onChange={(e) => setNotif({ ...notif, baa_reminder_days: [Number(e.target.value) || 0, notif.baa_reminder_days?.[1] ?? 7] })} />
                <input type="number" min="1" max="90" data-testid="baa-reminder-days-1" value={notif.baa_reminder_days?.[1] ?? 7}
                  onChange={(e) => setNotif({ ...notif, baa_reminder_days: [notif.baa_reminder_days?.[0] ?? 30, Number(e.target.value) || 0] })} />
              </div>
              <div className="text-[11px] text-white/40 mt-2">BAAs often need annual review — a longer lead (e.g. 30 days) gives time to re-sign before they lapse.</div>
              <button type="button" onClick={() => saveNotif()} disabled={notifSaving} data-testid="baa-reminder-days-save" className="btn-gold text-xs mt-3"><FloppyDisk size={14} weight="fill" /> {notifSaving ? "Saving…" : "Save BAA schedule"}</button>
            </div>
          </div>
        )}

        <button type="button" onClick={() => saveNotif({ ...notif, nexus_alerts_enabled: !notif.nexus_alerts_enabled })} data-testid="toggle-nexus-alerts"
          className="flex items-center gap-3 w-full rounded-xl bg-black/30 border border-white/5 p-4 hover:border-white/15">
          {notif.nexus_alerts_enabled ? <ToggleRight size={26} weight="fill" color="#C77DFF" /> : <ToggleLeft size={26} color="#8A9A94" />}
          <div className="text-left"><div className="text-sm font-semibold">Nexus alerts</div>
            <div className="text-xs text-white/50">Email me when a state&apos;s sales cross 80% of its registration threshold.</div></div>
        </button>

        <button type="button" onClick={() => saveNotif({ ...notif, auto_import_enabled: !notif.auto_import_enabled })} data-testid="toggle-auto-import"
          className="flex items-center gap-3 w-full rounded-xl bg-black/30 border border-white/5 p-4 mt-3 hover:border-white/15">
          {notif.auto_import_enabled ? <ToggleRight size={26} weight="fill" color="#00A86B" /> : <ToggleLeft size={26} color="#8A9A94" />}
          <div className="text-left"><div className="text-sm font-semibold">Nightly sales sync</div>
            <div className="text-xs text-white/50">Refresh each tracked state&apos;s sales from my invoices automatically every night.</div></div>
        </button>

        <div className="text-[11px] text-[#FFB020] mt-4 flex items-start gap-2"><ShieldWarning size={14} weight="fill" className="mt-0.5 shrink-0" /> Emails start delivering once your Resend sender domain (percen360.com) is verified and deployed.</div>
      </div>
    </div>
  );
}

function UsCard({ us, enabled, onAcknowledge }) {
  if (!enabled || !us || !us.obligations?.length) {
    return (
      <div className="glass rounded-2xl p-8" data-testid="us-card-empty">
        <div className="flex items-center gap-2 eyebrow mb-1"><Bank size={14} /> US deadlines</div>
        <div className="font-display text-2xl mb-3">Federal &amp; state reminders</div>
        <p className="text-sm text-white/55">Enable your US entity and set the incorporation date &amp; fiscal year end to auto-calculate Form 1120, estimated tax and your state&apos;s sales, income/franchise, withholding &amp; annual-report deadlines.</p>
      </div>
    );
  }
  return (
    <div className="glass rounded-2xl p-8" data-testid="us-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 eyebrow mb-1"><Bank size={14} /> US deadlines</div>
          <div className="font-display text-2xl">Federal &amp; state reminders</div>
        </div>
        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: "#C77DFF22", color: "#C77DFF", border: "1px solid #C77DFF" }}>US</span>
      </div>
      <div className="space-y-3">
        {us.obligations.map((o) => {
          const n = o.next; const rag = RAG[n.status] || RAG.green;
          const dr = n.days_remaining;
          const timeText = dr < 0 ? `${Math.abs(dr)}d overdue` : dr === 0 ? "Due today" : `${dr}d left`;
          return (
            <div key={o.type} className="rounded-xl p-4" style={{ background: `${rag.c}12`, border: `1px solid ${rag.c}40` }} data-testid={`us-ob-${o.type}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-white/85"><SealCheck size={16} weight="fill" style={{ color: rag.c }} /> {o.label}</div>
                <span className="mono text-sm font-semibold" style={{ color: rag.c }}>{timeText}</span>
              </div>
              <div className="text-xs text-white/55 mt-1.5 mono">{n.date} · {n.detail}</div>
              <button onClick={() => onAcknowledge(n.ack_key)} data-testid={`us-ack-${o.type}`} className="btn-ghost text-xs mt-3 w-full justify-center"><CheckCircle size={14} weight="fill" /> Mark filed</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UkCard({ uk, enabled, onAcknowledge }) {
  if (!enabled || !uk || !uk.obligations?.length) {
    return (
      <div className="glass rounded-2xl p-8" data-testid="uk-card-empty">
        <div className="flex items-center gap-2 eyebrow mb-1"><Bank size={14} /> UK deadlines</div>
        <div className="font-display text-2xl mb-3">Statutory reminders</div>
        <p className="text-sm text-white/55">Enable your UK entity and add the incorporation date to auto-calculate Confirmation Statement, accounts, Corporation Tax and VAT deadlines.</p>
      </div>
    );
  }
  return (
    <div className="glass rounded-2xl p-8" data-testid="uk-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 eyebrow mb-1"><Bank size={14} /> UK deadlines</div>
          <div className="font-display text-2xl">Statutory reminders</div>
        </div>
        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: "#5B8DEF22", color: "#5B8DEF", border: "1px solid #5B8DEF" }}>UK</span>
      </div>
      <div className="space-y-3">
        {uk.obligations.map((o) => {
          const n = o.next; const rag = RAG[n.status] || RAG.green;
          const dr = n.days_remaining;
          const timeText = dr < 0 ? `${Math.abs(dr)}d overdue` : dr === 0 ? "Due today" : `${dr}d left`;
          return (
            <div key={o.type} className="rounded-xl p-4" style={{ background: `${rag.c}12`, border: `1px solid ${rag.c}40` }} data-testid={`uk-ob-${o.type}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-white/85"><SealCheck size={16} weight="fill" style={{ color: rag.c }} /> {o.label}</div>
                <span className="mono text-sm font-semibold" style={{ color: rag.c }}>{timeText}</span>
              </div>
              <div className="text-xs text-white/55 mt-1.5 mono">{n.date} · {n.detail}</div>
              <button onClick={() => onAcknowledge(n.ack_key)} data-testid={`uk-ack-${o.type}`} className="btn-ghost text-xs mt-3 w-full justify-center"><CheckCircle size={14} weight="fill" /> Mark filed</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function computeNexus(threshold, sales, transactions) {
  if (!threshold || (!threshold.sales && !threshold.transactions)) return { applicable: false };
  const sVal = parseFloat(sales) || 0;
  const tVal = parseInt(transactions, 10) || 0;
  const sPct = threshold.sales ? Math.round((sVal / threshold.sales) * 1000) / 10 : null;
  const tPct = threshold.transactions ? Math.round((tVal / threshold.transactions) * 1000) / 10 : null;
  const pcts = [sPct, tPct].filter((p) => p !== null);
  const gating = threshold.logic === "and" && pcts.length > 1 ? Math.min(...pcts) : (pcts.length ? Math.max(...pcts) : 0);
  const status = gating >= 100 ? "red" : gating >= 80 ? "amber" : "green";
  return { applicable: true, sPct, tPct, gating, status };
}

function NexusTracker({ state, threshold, sales, transactions, onChange }) {
  if (!threshold) return null;
  const t = computeNexus(threshold, sales, transactions);
  if (!t.applicable) {
    return (
      <div className="mt-3 text-[11px] text-white/40" data-testid={`us-nexus-tracker-${state}`}>
        No statewide sales tax — no economic-nexus registration to track here.
      </div>
    );
  }
  const rag = RAG[t.status] || RAG.green;
  const barPct = Math.min(100, t.gating);
  const thText = threshold.sales
    ? `$${threshold.sales.toLocaleString()}${threshold.transactions ? (threshold.logic === "and" ? " AND " : " OR ") + threshold.transactions + " txns" : ""}`
    : `${threshold.transactions} txns`;
  return (
    <div className="mt-4 rounded-lg bg-black/30 border border-white/8 p-3" data-testid={`us-nexus-tracker-${state}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider text-white/45">Nexus tracker</div>
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: rag.c }} data-testid={`us-nexus-status-${state}`}>
          {t.status === "red" ? "Threshold reached" : t.status === "amber" ? "Approaching" : "Under threshold"}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <label className="text-[10px] text-white/40">Sales into {state} ($)</label>
          <input type="number" min="0" data-testid={`us-nexus-sales-${state}`} value={sales ?? ""}
            onChange={(e) => onChange("sales", e.target.value)} placeholder="0" />
        </div>
        {threshold.transactions ? (
          <div>
            <label className="text-[10px] text-white/40">Transactions</label>
            <input type="number" min="0" data-testid={`us-nexus-txn-${state}`} value={transactions ?? ""}
              onChange={(e) => onChange("transactions", e.target.value)} placeholder="0" />
          </div>
        ) : null}
      </div>
      <div className="h-2 rounded-full bg-white/8 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${barPct}%`, background: rag.c }} data-testid={`us-nexus-bar-${state}`} />
      </div>
      <div className="text-[11px] text-white/55 mt-2 leading-relaxed">
        <span className="mono" style={{ color: rag.c }}>{t.gating}%</span> of {state}&apos;s {threshold.logic === "and" ? "combined " : ""}nexus threshold ({thText}).
        {t.status === "red" && " You may have crossed it — confirm registration with the state / a US CPA."}
        {t.status === "amber" && " Getting close — keep an eye on this state."}
      </div>
    </div>
  );
}

function CroCard({ cro, onAcknowledge }) {
  if (!cro || !cro.incorporation_date) {
    return (
      <div className="glass rounded-2xl p-8">
        <div className="eyebrow mb-1">CRO Annual Return</div>
        <div className="font-display text-2xl mb-3">Reporting reminders</div>
        <p className="text-sm text-white/55">Enter your <strong>date of incorporation</strong> to auto-calculate your Annual Return Date (ARD) and 56-day filing deadlines, with a colour-coded reminder.</p>
      </div>
    );
  }
  const c = cro.current;
  const rag = RAG[c?.status] || RAG.green;
  const dr = c?.days_remaining ?? 0;
  const timeText = dr < 0 ? `Overdue by ${Math.abs(dr)} days` : dr === 0 ? "Due today" : `${dr} days left (${c.weeks_remaining} wks)`;

  return (
    <div className="glass rounded-2xl p-8" data-testid="cro-card" style={{ borderColor: `${rag.c}55` }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="eyebrow mb-1">CRO Annual Return</div>
          <div className="font-display text-2xl">Reporting reminders</div>
        </div>
        <span className="px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider" style={{ background: `${rag.c}22`, color: rag.c, border: `1px solid ${rag.c}` }} data-testid="cro-status">{rag.label}</span>
      </div>

      <div className="rounded-xl p-5 mb-4" style={{ background: `${rag.c}12`, border: `1px solid ${rag.c}40` }}>
        <div className="flex items-center gap-2 text-sm text-white/80"><SealCheck size={18} weight="fill" style={{ color: rag.c }} /> Return #{c.return_number}{c.is_first_return ? " (first — no accounts)" : " (accounts required)"}</div>
        <div className="mt-3 flex items-baseline gap-3">
          <div className="mono text-2xl" style={{ color: rag.c }} data-testid="cro-time">{timeText}</div>
        </div>
        <div className="text-xs text-white/55 mt-2">ARD <span className="mono text-white/80">{c.ard}</span> · Filing deadline <span className="mono text-white/80" data-testid="cro-deadline">{c.filing_deadline}</span></div>
        <button onClick={() => onAcknowledge(c.filing_deadline)} data-testid="cro-acknowledge" className="btn-gold mt-4 w-full justify-center"><CheckCircle size={16} weight="fill" /> Acknowledge submission (mark filed)</button>
      </div>

      <div className="eyebrow mb-2">Upcoming</div>
      <div className="space-y-2">
        {cro.upcoming.slice(0, 3).map((p) => {
          const pr = RAG[p.status] || RAG.green;
          return (
            <div key={p.filing_deadline} className="flex items-center justify-between text-sm py-2 border-b border-white/5" data-testid={`cro-upcoming-${p.return_number}`}>
              <span className="text-white/70">Return #{p.return_number} · <span className="mono text-xs">{p.filing_deadline}</span></span>
              <span className="text-xs font-semibold" style={{ color: pr.c }}>{p.days_remaining < 0 ? `${Math.abs(p.days_remaining)}d overdue` : `${p.days_remaining}d`}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
